import React, { useState } from 'react';
import { Phone, MapPin, ShieldCheck, ChevronDown, Shield } from 'lucide-react';

interface FooterProps {
  onNavigateAdminPortal?: () => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigateAdminPortal }) => {
  const [familyOpen, setFamilyOpen] = useState(false);

  const handleFamilySelect = (action: string) => {
    setFamilyOpen(false);
    if (action === 'admin-portal') {
      if (onNavigateAdminPortal) {
        onNavigateAdminPortal();
      }
    } else if (action.startsWith('http')) {
      window.open(action, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300 pt-16 pb-24 md:pb-16 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-slate-800">
          
          {/* Col 1: Brand Info */}
          <div className="md:col-span-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-white p-1 shadow-md flex items-center justify-center overflow-hidden shrink-0">
                <img src="/plus_sd_logo.png" alt="플러스에스디" className="w-full h-full object-contain" />
              </div>
              <div>
                <span className="text-xl font-black text-white tracking-tight leading-tight block">
                  플러스에스디
                </span>
                <span className="block text-[9px] text-slate-400 font-bold tracking-widest uppercase">
                  SOLAR & ROOF AGENCY
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-400 leading-relaxed max-w-sm">
              플러스에스디는 유휴 지붕과 유휴 부지를 활용하여 최적의 임대 수익과 건물 보수 지원 혜택을 설계해 드리는 친환경 태양광 전문 플랫폼입니다.
            </p>

            <div className="flex items-center gap-2 text-xs text-sky-400 font-semibold pt-1">
              <ShieldCheck className="w-4 h-4 text-sky-400" />
              <span>100% 1등급 검증 협력사 시공 및 1:1 진단 지원</span>
            </div>
          </div>

          {/* Col 2: Quick Links */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">주요 서비스</h4>
            <ul className="space-y-2 text-xs text-slate-400">
              <li><a href="#benefits" className="hover:text-sky-400 transition-colors">지붕임대 수리비 지원</a></li>
              <li><a href="#eligibility" className="hover:text-sky-400 transition-colors">설치 조건 & 절차 안내</a></li>
              <li><a href="#consultation" className="hover:text-sky-400 transition-colors">실시간 수익 계산기</a></li>
              <li><a href="#consultation" className="hover:text-sky-400 transition-colors">1:1 간편 견적 신청</a></li>
            </ul>
          </div>

          {/* Col 3: Direct Inquiry & Family Site Dropdown */}
          <div className="md:col-span-4 space-y-4">
            <div>
              <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-2">상담 문의 & 전담 창구</h4>
              <div className="space-y-2 text-xs text-slate-400">
                <a
                  href="tel:01027671449"
                  className="inline-flex items-center gap-2 text-sky-400 font-black text-lg hover:underline"
                >
                  <Phone className="w-5 h-5 text-sky-400" />
                  <span>010-2767-1449</span>
                </a>
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <span>서비스 가능 지역: 전국 어디서나 (전국 전 지역)</span>
                </p>
              </div>
            </div>

            {/* 패밀리사이트 바로가기 메뉴 */}
            <div className="pt-2">
              <h4 className="text-xs font-bold text-slate-400 mb-1.5">패밀리사이트 바로가기</h4>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setFamilyOpen(!familyOpen)}
                  className="w-full max-w-xs px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-slate-700 text-slate-300 text-xs font-bold flex items-center justify-between transition-colors cursor-pointer shadow-sm"
                >
                  <span>패밀리사이트 선택</span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${familyOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {familyOpen && (
                  <div className="absolute left-0 bottom-full mb-1.5 w-full max-w-xs bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden py-1 z-50 animate-in fade-in">
                    {/* Admin Portal Hub Link Only */}
                    <button
                      type="button"
                      onClick={() => handleFamilySelect('admin-portal')}
                      className="w-full px-3.5 py-2.5 text-left text-xs font-extrabold text-sky-400 hover:bg-slate-900 flex items-center gap-2 cursor-pointer transition-colors"
                    >
                      <Shield className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                      <span>[관리자] +SD 관리자 전용 포털</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>

        {/* Business Info (필수 사업자 정보 표기) */}
        <div className="pt-8 pb-6 border-b border-slate-800 text-xs text-slate-400 leading-relaxed space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span><strong className="text-slate-300 font-semibold">상호 :</strong> 주식회사 신동컴퍼니</span>
            <span className="hidden sm:inline text-slate-700">|</span>
            <span><strong className="text-slate-300 font-semibold">대표자 :</strong> 신동현</span>
            <span className="hidden sm:inline text-slate-700">|</span>
            <span><strong className="text-slate-300 font-semibold">사업자등록번호 :</strong> 375-87-03353</span>
          </div>
          <div>
            <span><strong className="text-slate-300 font-semibold">사업장 주소 :</strong> 경기도 의정부시 경의로 86, 2층(의정부동)</span>
          </div>
        </div>

        {/* Bottom Notice */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-500 gap-4">
          <p>© 2026 +SD PLUS SD SOLAR AGENCY. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => onNavigateAdminPortal && onNavigateAdminPortal()}
              className="text-slate-500 hover:text-sky-400 cursor-pointer font-bold transition-colors"
            >
              [관리자 포털]
            </button>
            <span>|</span>
            <a href="#consultation" className="hover:text-sky-400 transition-colors">개인정보처리방침</a>
            <span>|</span>
            <a href="#consultation" className="hover:text-sky-400 transition-colors">이용약관</a>
            <span>|</span>
            <a href="tel:01027671449" className="hover:text-sky-400 transition-colors">010-2767-1449 직통 연결</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
